import { routes } from '@/config/routes'
import type { Rewards } from '@/queries/rewards.query'
import type { SdkNetwork } from '@/types/network'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { formatPreciseTokenValue, formatUSD } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { Link, generatePath } from 'react-router'
import { NetworkImage } from '../common/networkImage'
import { Separator } from '../common/separator'
import { TokenImage } from '../common/tokenImage'
import { ExternalLinkIcon } from '../icons/externalLink'
import { RewardsIcon } from '../icons/rewards'
import { RewardsDialog } from './rewardsDialog'

interface Props {
  rewards: NonNullable<Rewards>
  className?: string
}

function useTotalClaimable(rewards: NonNullable<Rewards>) {
  return useMemo(() => {
    return Object.values(rewards).reduce((total, rewardsByNetwork) => {
      return (
        total +
        rewardsByNetwork.reduce((networkTotal, reward) => {
          return networkTotal + parseFloat(reward.asset.claimableAmountInUsd || '0')
        }, 0)
      )
    }, 0)
  }, [rewards])
}

export const RewardsPanelHeader = ({ rewards, className }: Props) => {
  const totalClaimableAmountInUsd = useTotalClaimable(rewards)

  return (
    <header className={cn('px-4 md:px-5', className)}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <RewardsIcon size={20} className="shrink-0 text-accent" />
            <h3 className="font-medium text-base text-heading-foreground">Claimable rewards</h3>
          </div>
          {totalClaimableAmountInUsd === 0 && <p className="text-body-foreground text-sm">No rewards to claim now.</p>}
        </div>
        <p
          className={cn(
            'shrink-0 font-semibold text-body-foreground tabular-nums leading-none md:text-2xl lg:text-4xl',
            totalClaimableAmountInUsd === 0 && 'opacity-55'
          )}
        >
          {formatUSD(totalClaimableAmountInUsd, { notation: 'standard' })}
        </p>
      </div>
    </header>
  )
}

export const RewardsPanelContent = ({ rewards, className }: Props) => {
  return (
    <ul className={cn('flex flex-col gap-3 px-4 md:px-5', className)}>
      {Object.keys(rewards).map((network) => (
        <li key={network} className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-body-foreground">
            <NetworkImage network={network} className="size-5" />
            <p className="capitalize">{getNetworkName(network)}</p>
          </div>
          <ul className="flex flex-col gap-2">
            {rewards[network as SdkNetwork]?.map(({ asset }) => (
              <li
                key={asset.address}
                className="flex items-center gap-2 overflow-hidden rounded-xl border bg-page px-3 py-2"
              >
                <TokenImage symbol={asset.symbol} className="size-8 shrink-0" />
                <span className="flex-1 font-medium text-heading-foreground text-sm">${asset.symbol}</span>
                <p className="text-body-foreground text-sm tabular-nums">
                  {asset.claimableAmountInUsd
                    ? formatUSD(asset.claimableAmountInUsd, { notation: 'standard' })
                    : formatPreciseTokenValue({ value: asset.claimableAmount, decimals: asset.decimals })}
                </p>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}

export const RewardsPanelFooter = ({ rewards, className }: Props) => {
  const totalClaimableAmountInUsd = useTotalClaimable(rewards)

  if (totalClaimableAmountInUsd === 0) {
    return (
      <footer className={cn('p-4 md:p-5', className)}>
        <Link
          to={generatePath(routes.root)}
          className="flex items-center justify-end gap-1 text-accent text-sm hocus:underline"
        >
          Browse vaults that offer rewards
          <ExternalLinkIcon size={16} />
        </Link>
      </footer>
    )
  }

  return (
    <footer className={cn('flex flex-col gap-3 px-4 pb-4 md:px-5 md:pb-5', className)}>
      <Separator />
      <RewardsDialog rewards={rewards} />
    </footer>
  )
}
